import { Icon, IconName, IconSize, IconVariant, useMe } from "@quortex/front-common"
import { useNavigate, useSearch } from "@tanstack/react-location"
import { useSuspenseQuery } from "@tanstack/react-query"
import {
  type Edge,
  type Node,
  Position,
  ReactFlow,
  useNodesInitialized,
  useReactFlow
} from "@xyflow/react"
import { stratify, tree } from "d3-hierarchy"
import { type FC, useCallback, useEffect, useMemo, useRef } from "react"
import "./SegmentationFlow.scss"
import { useShallow } from "zustand/react/shallow"
import type { SegmentationExpandedGenerics } from "@/pages/ContentSteering/Segmentation"
import SegmentationNode, {
  type NodeData
} from "@/pages/ContentSteering/Segmentation/SegmentationFlow/SegmentNode"
import { useCdnList } from "@/queries/cdn"
import { usePreferences } from "@/stores/preferences"
import { useSegmentationFallbackTour } from "@/stores/segmentationFallbackTour"
import { useSegmentationFlowStore } from "@/stores/segmentationFlow"
import { useSegmentationRootTour } from "@/stores/segmentationRootTour"
import type { BalancingResponse, BalancingWithCdnStatus, SegmentResponse } from "@/types/api"
import NewSegmentEdge from "./NewSegmentEdge"
import NewSegmentationNode from "./NewSegmentNode"
import SegmentEdge from "./SegmentEdge"

interface SegmentationFlowProps {
  segments: SegmentResponse[]
}

type NewNode = {
  parentUuid: string
}

type CdnMap = {
  [key: string]: api.switchns.Cdn
}

const nodeTypes = { newSegmentNode: NewSegmentationNode, segmentNode: SegmentationNode }
const edgeTypes = { newSegmentEdge: NewSegmentEdge, segmentEdge: SegmentEdge }

const g = tree<Node>()

const nodesHaveSameLayout = (
  currentNodes: Node<NodeData | NewNode>[],
  nextNodes: Node<NodeData | NewNode>[]
) =>
  currentNodes.length === nextNodes.length &&
  currentNodes.every((node, index) => {
    const nextNode = nextNodes[index]
    return (
      nextNode &&
      node.id === nextNode.id &&
      node.position.x === nextNode.position.x &&
      node.position.y === nextNode.position.y
    )
  })

const edgesHaveSameLayout = (currentEdges: Edge[], nextEdges: Edge[]) =>
  currentEdges.length === nextEdges.length &&
  currentEdges.every((edge, index) => {
    const nextEdge = nextEdges[index]
    return (
      nextEdge &&
      edge.id === nextEdge.id &&
      edge.source === nextEdge.source &&
      edge.target === nextEdge.target
    )
  })

const getLayoutedElements = (nodes: Node<NodeData | NewNode>[], edges: Edge[]) => {
  if (nodes.length === 0) return { edges, nodes }

  const width = 208
  const xGap = 100
  const height = 80
  const yGap = 25

  const hierarchy = stratify<Node>()
    .id(node => node.id)
    .parentId(node => edges.find(edge => edge.target === node.id)?.source ?? null)
  const root = hierarchy(nodes)
  const layout = g.nodeSize([height + yGap, width + xGap])(root)

  return {
    edges,
    nodes: layout.descendants().map(node => ({ ...node.data, position: { x: node.y, y: node.x } }))
  }
}

const SegmentationFlow: FC<SegmentationFlowProps> = ({ segments }) => {
  const navigate = useNavigate()
  const { sideBarCollapsed, setSegmentationSideBarCollapsed } = usePreferences()
  const { setCenter, zoomIn, zoomOut, getViewport, setViewport } = useReactFlow()
  const [
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    setEdges,
    setNodes,
    currentViewport,
    setCurrentViewport
  ] = useSegmentationFlowStore(
    useShallow(s => [
      s.nodes,
      s.edges,
      s.onNodesChange,
      s.onEdgesChange,
      s.setEdges,
      s.setNodes,
      s.currentViewport,
      s.setCurrentViewport
    ])
  )
  const nodesInitialized = useNodesInitialized()
  const rootStep = useSegmentationRootTour(state => state.steps[0])
  const fallbackStep = useSegmentationFallbackTour(state => state.steps[0])
  const { data: me } = useSuspenseQuery(useMe())
  const seenTours = me.seenTours
  const nodesRef = useRef(nodes)
  const edgesRef = useRef(edges)
  const currentViewportRef = useRef(currentViewport)

  const search = useSearch<SegmentationExpandedGenerics>()

  useEffect(() => {
    nodesRef.current = nodes
  }, [nodes])

  useEffect(() => {
    edgesRef.current = edges
  }, [edges])

  useEffect(() => {
    currentViewportRef.current = currentViewport
  }, [currentViewport])

  const handlePositioning = useCallback(() => {
    const currentNodes = nodesRef.current
    const currentEdges = edgesRef.current
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      currentNodes,
      currentEdges
    )
    const nextNodes = layoutedNodes as Node<NodeData | NewNode>[]

    if (!nodesHaveSameLayout(currentNodes, nextNodes)) setNodes(nextNodes)
    if (!edgesHaveSameLayout(currentEdges, layoutedEdges)) setEdges(layoutedEdges)

    const minX = Math.min(...layoutedNodes.map(node => node.position.x))
    const minY = Math.min(...layoutedNodes.map(node => node.position.y))
    const maxX = Math.max(
      ...layoutedNodes.map(node => node.position.x + (node.measured?.width ?? 0))
    )
    const maxY = Math.max(
      ...layoutedNodes.map(node => node.position.y + (node.measured?.height ?? 0))
    )

    const centerX = (minX + maxX) / 2
    const centerY = (minY + maxY) / 2

    // use saved viewport only if there is more than the root node
    if (currentViewportRef.current && currentNodes.length > 1) {
      setViewport(currentViewportRef.current)
    } else {
      setCenter(centerX, centerY, { zoom: 1 })
    }
  }, [setNodes, setEdges, setViewport, setCenter])

  const toggleFullscreenRequest = useCallback(() => {
    if (setCurrentViewport) {
      setCurrentViewport(null)
      setTimeout(handlePositioning, 1000)
    }

    if (search.view !== "segmentationExpanded" || !sideBarCollapsed) {
      setSegmentationSideBarCollapsed(search.view !== "segmentationExpanded")
    }

    navigate({
      replace: true,
      search: {
        view: search.view === "segmentationExpanded" ? "" : "segmentationExpanded"
      }
    })
  }, [
    setCurrentViewport,
    handlePositioning,
    search.view,
    sideBarCollapsed,
    setSegmentationSideBarCollapsed,
    navigate
  ])

  const shouldBeHighlighted = useCallback(
    (data: NodeData) => {
      const rootUuid = segments.find(segment => segment.type === "root")?.uuid
      const rootFallbackUuid = segments.find(
        segment => segment.isFallback && segment.parentUuid === rootUuid
      )?.uuid
      const isRoot = data.type === "root"
      const isFallback = data.isFallback && data.uuid === rootFallbackUuid
      const hasSeenRootTour = seenTours.includes("switch_root_segment")
      const hasSeenFallbackTour = seenTours.includes("switch_fallback_segment")

      if (isRoot) return !hasSeenRootTour
      if (isFallback) return hasSeenRootTour && !hasSeenFallbackTour
      return false
    },
    [segments, seenTours]
  )

  const { data: cdnList } = useSuspenseQuery({
    ...useCdnList(),
    refetchInterval: 10_000,
    select: d => d.results
  })

  const cdnMap = useMemo(
    () =>
      cdnList.reduce((map: CdnMap, cdn: api.switchns.Cdn) => {
        map[cdn.uuid] = cdn
        return map
      }, {} as CdnMap),
    [cdnList]
  )

  useEffect(() => {
    const onEscapeButton = (event: KeyboardEvent) => {
      if (event.key === "Escape" && search.view === "segmentationExpanded")
        toggleFullscreenRequest()
    }

    document.addEventListener("keydown", onEscapeButton)

    return () => {
      document.removeEventListener("keydown", onEscapeButton)
    }
  }, [search, toggleFullscreenRequest])

  useEffect(() => {
    setNodes(
      nodes.map(node => {
        return {
          ...node,
          data: {
            ...node.data,
            isHighlighted:
              node.type !== "newSegmentNode" ? shouldBeHighlighted(node.data as NodeData) : false
          }
        }
      })
    )
  }, [setNodes, nodes.map, shouldBeHighlighted])

  useEffect(() => {
    const miscNodes: Node<NodeData>[] = []
    const newSegmentNodes: Node<NewNode>[] = []
    const rootUuid = segments.find(segment => segment.type === "root")?.uuid
    const perParentChildRegions: Record<
      string,
      (api.switchns.CountryValue["code"] | api.switchns.SubdivisionValue["code"])[]
    > = {}

    segments.forEach(segment => {
      let node: Node<NodeData> = {
        data: {
          cdnList: (segment.balancings as BalancingResponse[]).map(
            (balancing): BalancingWithCdnStatus => ({
              ...balancing,
              qoeStatus: cdnMap[balancing.cdnUuid]?.qoeStatus
            })
          ),
          children: segment.childrenUuids,
          devices: segment.devices,
          isFallback: segment.isFallback,
          isHighlighted: segment.type === "root" && !seenTours.includes("switch_root_segment"),
          isLastChild:
            segments.filter(s => !s.isFallback && s.parentUuid === segment.parentUuid).length <= 1,
          label: segment.name ?? "",
          network: segment.network,
          parentUuid: segment.parentUuid,
          protocol: segment.protocol,
          ref:
            segment.uuid === rootUuid
              ? rootStep?.setTargetElement
              : !fallbackStep?.getTargetElement() && segment.isFallback
                ? fallbackStep?.setTargetElement
                : undefined,
          regions:
            segment.geolocation?.countries?.length || segment.geolocation?.subdivisions?.length
              ? [
                  ...(segment.geolocation?.countries || []),
                  ...(segment.geolocation?.subdivisions || [])
                ]
              : undefined,
          type: segment.type,
          uuid: segment.uuid
        },
        id: segment.uuid,
        position: { x: 0, y: 0 },
        targetPosition: Position.Left,
        type: "segmentNode"
      }

      if ((segment.parentUuid || segment.type === "root") && segment.childrenUuids.length > 0) {
        const newSegmentNode: Node<NewNode> = {
          data: {
            parentUuid: segment.uuid
          },
          id: `new-segment-${segment.uuid}`,
          position: { x: 0, y: 0 },
          type: "newSegmentNode"
        }

        node = {
          ...node,
          data: {
            ...node.data,
            cdnList: [],
            children: [...node.data.children, newSegmentNode.id]
          }
        }

        miscNodes.push(node)
        newSegmentNodes.push(newSegmentNode)

        return
      }

      miscNodes.push(node)
    })

    Object.keys(perParentChildRegions).forEach(parentUuid => {
      const fallbackIndex = miscNodes.findIndex(
        node => node.data.parentUuid === parentUuid && node.data.isFallback
      )

      if (fallbackIndex >= 0 && perParentChildRegions[parentUuid] && miscNodes[fallbackIndex]) {
        miscNodes[fallbackIndex].data.regions = perParentChildRegions[parentUuid]
      }
    })

    const edges = [...miscNodes]
      .filter(node => node.data.children.length > 0)
      .flatMap(node =>
        node.data.children.flatMap(
          (child): Edge => ({
            animated: false,
            id: `${node.id}-${child}`,
            source: node.id,
            sourceHandle: "output",
            target: child,
            targetHandle: "input",
            type: child.includes("new-segment-") ? "newSegmentEdge" : "segmentEdge",
            ...(child.includes("new-segment-") ? { data: { show: false } } : {})
          })
        )
      )

    // sort nodes
    setNodes(
      [...miscNodes, ...newSegmentNodes].sort(
        (segmentA: Node<NodeData | NewNode>, segmentB: Node<NodeData | NewNode>) => {
          if ("isFallback" in segmentA.data && segmentA.data.isFallback) return 1
          if ("isFallback" in segmentB.data && segmentB.data.isFallback) return -1
          if (segmentA.type === "newSegmentEdge") return 1
          if (segmentB.type === "newSegmentEdge") return -1
          return 0
        }
      )
    )
    setEdges(edges)
  }, [
    segments,
    setEdges,
    seenTours.includes,
    rootStep?.setTargetElement,
    setNodes,
    fallbackStep?.setTargetElement,
    fallbackStep?.getTargetElement,
    cdnMap
  ])

  useEffect(() => {
    if (nodesInitialized) {
      handlePositioning()
    }
  }, [nodesInitialized, handlePositioning])

  return (
    <div className="relative h-full w-full">
      <ReactFlow
        autoPanOnConnect={false}
        autoPanOnNodeDrag={false}
        className="cursor-default!"
        connectOnClick={false}
        edges={edges}
        edgesFocusable={false}
        edgeTypes={edgeTypes}
        elementsSelectable={false}
        fitView
        nodes={nodes}
        nodesConnectable={false}
        nodesDraggable={false}
        nodesFocusable={false}
        nodeTypes={nodeTypes}
        panOnDrag
        selectionOnDrag={false}
        selectNodesOnDrag={false}
        zoomOnDoubleClick={false}
        zoomOnPinch={false}
        onEdgesChange={onEdgesChange}
        onMoveEnd={() => {
          const viewportChanged = getViewport()
          if (viewportChanged.x !== 0) setCurrentViewport(getViewport())
        }}
        onNodeClick={e => e.preventDefault()}
        onNodesChange={onNodesChange}
      />

      <div className="absolute top-sm right-sm flex flex-col">
        <div className="radius-2xl mb-xs flex flex-col overflow-hidden border border-grey-300 bg-white shadow-md">
          <button
            className="flex p-xs text-xl hover:bg-grey-200"
            data-testid="segmentation-flow-toggle-fullscreen-button"
            type="button"
            onClick={() => toggleFullscreenRequest()}
          >
            {search.view === "segmentationExpanded" ? (
              <Icon
                className="text-black"
                icon={IconName.SCALE_DOWN}
                size={IconSize.SMALL}
                type={IconVariant.SOLID}
              />
            ) : (
              <Icon
                className="text-black"
                icon={IconName.SCALE_UP}
                size={IconSize.SMALL}
                type={IconVariant.SOLID}
              />
            )}
          </button>
        </div>
        <div className="radius-2xl flex flex-col overflow-hidden border border-grey-300 bg-white shadow-md">
          <button
            className="flex p-xs text-xl hover:bg-grey-200"
            data-testid="segmentation-flow-zoom-in-button"
            type="button"
            onClick={() => zoomIn({ duration: 400 })}
          >
            <Icon
              className="text-black"
              icon={IconName.PLUS}
              size={IconSize.SMALL}
              type={IconVariant.SOLID}
            />
          </button>
          <hr className="mb-0! bg-grey-300" />
          <button
            className="flex p-xs hover:bg-grey-200"
            data-testid="segmentation-flow-zoom-out-button"
            type="button"
            onClick={() => zoomOut({ duration: 400 })}
          >
            <Icon
              className="text-black"
              icon={IconName.MINUS}
              size={IconSize.SMALL}
              type={IconVariant.SOLID}
            />
          </button>
        </div>
      </div>
    </div>
  )
}

export default SegmentationFlow
